import { useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, buildAccessUrl, type AppInfo } from '../api/client';
import StatusBadge, { statusLabel } from '../components/StatusBadge';
import {
  AlertIcon,
  ArrowLeftIcon,
  DownloadIcon,
  ExternalIcon,
  GlobeIcon,
  PackageIcon,
  RefreshIcon,
} from '../components/icons';
import { clampPct, firstChar, formatTime, iconGradientClass } from '../lib/utils';

export default function AppDetail() {
  const { id } = useParams<{ id: string }>();
  const queryClient = useQueryClient();

  const { data: app, isLoading, error, refetch, isFetching } = useQuery({
    queryKey: ['app', id],
    queryFn: () => api.getApp(id!),
    enabled: !!id,
    // 构建中 / 上传中时每 3 秒轮询一次，其他状态停止轮询
    refetchInterval: (query) => {
      const data = query.state.data;
      if (data && (data.status === 'building' || data.status === 'uploading')) {
        return 3000;
      }
      return false;
    },
  });

  const retry = useMutation({
    mutationFn: () => api.triggerBuild(id!),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['app', id] });
    },
  });

  if (isLoading) {
    return (
      <div className="page-wrap">
        <div className="state-box">加载中…</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="page-wrap">
        <Link to="/" className="back-link">
          <ArrowLeftIcon size={16} />
          返回应用列表
        </Link>
        <div className="state-box state-error">
          <span>加载失败：{(error as Error).message}</span>
          <button className="btn btn-ghost btn-sm" onClick={() => refetch()}>
            重试
          </button>
        </div>
      </div>
    );
  }

  if (!app || !id) {
    return (
      <div className="page-wrap">
        <div className="state-box">未找到该应用。</div>
      </div>
    );
  }

  const accessUrl = buildAccessUrl(app.id);
  const isBuilding = app.status === 'building' || app.status === 'uploading';

  const cardTitle = isBuilding
    ? '部署进度'
    : app.status === 'success'
      ? '部署完成'
      : app.status === 'failed'
        ? '部署失败'
        : '应用状态';

  return (
    <div className="page-wrap">
      <Link to="/" className="back-link">
        <ArrowLeftIcon size={16} />
        返回应用列表
      </Link>

      <div className="app-header">
        <div className={`app-icon ${iconGradientClass(app.name)}`}>
          {firstChar(app.name)}
        </div>
        <div className="app-header-info">
          <h1 className="detail-title">{app.name}</h1>
          <div className="app-repo" title={app.id}>{app.id}</div>
        </div>
        <div className="app-header-status">
          <StatusBadge status={app.status} />
        </div>
      </div>

      <div className="card">
        <div className="card-head">
          <span className="card-title">{cardTitle}</span>
          {isFetching && !isBuilding && <span className="refresh-hint-inline">刷新中…</span>}
        </div>

        {isBuilding && <BuildingView app={app} />}
        {app.status === 'success' && (
          <SuccessView appId={app.id} accessUrl={accessUrl} buildLog={app.buildLog} />
        )}
        {app.status === 'failed' && (
          <FailedView
            app={app}
            onRetry={() => retry.mutate()}
            retrying={retry.isPending}
          />
        )}
        {app.status === 'idle' && <IdleView onBuild={() => retry.mutate()} retrying={retry.isPending} />}
      </div>

      <div className="card">
        <div className="card-head">
          <span className="card-title">应用信息</span>
        </div>
        <dl className="info-list">
          <div className="info-row">
            <dt>名称</dt>
            <dd>{app.name}</dd>
          </div>
          <div className="info-row">
            <dt>状态</dt>
            <dd>{statusLabel(app.status)}</dd>
          </div>
          <div className="info-row">
            <dt>创建时间</dt>
            <dd>{formatTime(app.createdAt)}</dd>
          </div>
          <div className="info-row">
            <dt>最后构建</dt>
            <dd>{formatTime(app.lastBuildAt)}</dd>
          </div>
        </dl>
      </div>
    </div>
  );
}

function BuildingView({ app }: { app: AppInfo }) {
  const pct = clampPct(app.progress);
  return (
    <div>
      <div className="progress-bar-wrap">
        <div className="progress-bar">
          <div className="progress-bar-fill" style={{ width: `${pct}%` }} />
        </div>
        <div className="progress-label">
          <span>{app.currentStep ?? '正在构建…'}</span>
          <span>{pct}%</span>
        </div>
      </div>

      {app.buildLog && (
        <pre className="log-box">{app.buildLog}</pre>
      )}

      <p className="building-hint">
        <span className="status-dot dot-building" /> 构建中，页面每 3 秒自动刷新状态…
      </p>
    </div>
  );
}

function SuccessView({
  appId,
  accessUrl,
  buildLog,
}: {
  appId: string;
  accessUrl: string;
  buildLog?: string | null;
}) {
  const [apkState, setApkState] = useState<'idle' | 'downloading' | 'error'>('idle');
  const [apkError, setApkError] = useState('');

  async function handleDownloadApk() {
    setApkState('downloading');
    setApkError('');
    try {
      await api.downloadApk(appId);
      setApkState('idle');
    } catch (err) {
      setApkState('error');
      setApkError((err as Error).message || '下载失败');
    }
  }

  return (
    <div>
      <div className="ready-grid">
        <div className="ready-block">
          <div className="ready-block-label">在线访问</div>
          <a className="vercel-link" href={accessUrl} target="_blank" rel="noreferrer">
            <GlobeIcon size={14} />
            <span>{accessUrl.replace('https://', '')}</span>
            <ExternalIcon size={12} />
          </a>
        </div>
        <div className="ready-block">
          <div className="ready-block-label">下载 APK</div>
          <button
            className="vercel-link"
            onClick={handleDownloadApk}
            disabled={apkState === 'downloading'}
            style={{ background: 'none', border: 'none', cursor: apkState === 'downloading' ? 'wait' : 'pointer' }}
          >
            <DownloadIcon size={14} />
            <span>
              {apkState === 'downloading' ? '下载中…' : apkState === 'error' ? '重试下载' : '点击下载 APK'}
            </span>
          </button>
          {apkState === 'error' && (
            <div className="field-error" style={{ marginTop: '4px' }}>{apkError}</div>
          )}
        </div>
      </div>

      <div className="install-guide">
        <h4 className="install-title">
          <PackageIcon size={16} /> 手机安装（Android）
        </h4>
        <ol className="install-steps">
          <li>点击上方"下载 APK"链接，下载安装文件</li>
          <li>系统提示"允许安装未知应用"时，点击允许</li>
          <li>点击安装，等待安装完成</li>
          <li>打开 App，开始使用</li>
        </ol>
      </div>

      {buildLog && <pre className="log-box">{buildLog}</pre>}
    </div>
  );
}

function FailedView({
  app,
  onRetry,
  retrying,
}: {
  app: AppInfo;
  onRetry: () => void;
  retrying: boolean;
}) {
  return (
    <div>
      <div className="error-box">
        <h3 className="error-title">
          <AlertIcon size={16} /> 构建失败
        </h3>
        <div className="error-detail">
          {app.errorMessage ?? '未知错误，请查看构建日志或重试。'}
        </div>
      </div>

      {app.buildLog && <pre className="log-box">{app.buildLog}</pre>}

      <div className="form-actions">
        <button className="btn btn-accent btn-sm" onClick={onRetry} disabled={retrying}>
          <RefreshIcon size={14} />
          {retrying ? '重试中…' : '修复后重试'}
        </button>
      </div>
    </div>
  );
}

function IdleView({ onBuild, retrying }: { onBuild: () => void; retrying: boolean }) {
  return (
    <div>
      <div className="state-box">该应用尚未触发构建。</div>
      <div className="form-actions">
        <button className="btn btn-primary btn-sm" onClick={onBuild} disabled={retrying}>
          <PackageIcon size={14} />
          {retrying ? '触发中…' : '开始构建'}
        </button>
      </div>
    </div>
  );
}
